import { Handle, Position } from "@xyflow/react";
import i18n from "@/i18n";

const nodeBase = "min-w-[260px] bg-[#111] border border-[#27272A] rounded-lg shadow-xl hover:ring-1 hover:ring-primary/50 transition-shadow duration-200";

const NodeHeader = ({ color, label, icon }) => (
  <div className="relative">
    <div className="h-1.5 w-full rounded-t-lg" style={{ backgroundColor: color }} />
    <div className="p-3 pb-0 flex items-center gap-2">
      <span style={{ color }} className="text-sm">{icon}</span>
      <span className="text-sm font-semibold text-foreground">{label}</span>
    </div>
  </div>
);

export const StartNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-start">
    <NodeHeader color="#0A84FF" label={data.label || "Start"} icon="▶" />
    <div className="p-3 text-xs text-muted-foreground">Workflow begins here</div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const ObjectiveNode = ({ data }) => {
  const isGather = data.config?.objective_type === "gather";
  const typeLabel = isGather ? "Gather Info" : "Give Info";
  const typeColor = isGather ? "#FFD60A" : "#0A84FF";
  const prefill = data.config?.prefill_behavior || "always_ask";
  const prefillLabels = { always_ask: "Always ask", confirm_if_filled: "Confirm existing", skip_if_filled: "Skip if filled" };

  return (
    <div className={nodeBase} data-testid="node-objective">
      <NodeHeader color="#00E676" label={data.label || "Objective"} icon="◎" />
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: `${typeColor}20`, color: typeColor }}>
            {typeLabel}
          </span>
          {isGather && prefill !== "always_ask" && (
            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#BF5AF2]/15 text-[#BF5AF2]">
              {prefillLabels[prefill]}
            </span>
          )}
        </div>
        {isGather ? (
          <>
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">Collect:</span> {data.config?.field_label || data.config?.field_name || "Not set"}
            </div>
            {data.config?.ghl_custom_field && (
              <div className="text-xs text-muted-foreground font-mono">
                <span className="text-primary/70">CRM:</span> {data.config.ghl_custom_field}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-muted-foreground line-clamp-2">
            {data.config?.message_prompt || "No prompt set"}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    </div>
  );
};

export const ConversationNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-conversation">
    <NodeHeader color="#00E676" label={data.label || "Conversation"} icon="💬" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 text-xs text-muted-foreground">
      Free chat using Knowledge Base. Max turns: {data.config?.max_turns || 5}
    </div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const ConditionalNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-conditional">
    <NodeHeader color="#FFD60A" label={data.label || "Condition"} icon="◇" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 space-y-1">
      <div className="text-xs text-muted-foreground">
        If <span className="text-[#FFD60A] font-medium">{data.config?.field || "field"}</span>{" "}
        {data.config?.operator || "equals"}{" "}
        <span className="text-[#FFD60A] font-medium">{data.config?.value || "value"}</span>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="true" style={{ left: "30%" }} className="!w-3 !h-3 !bg-[#00E676] !border-2 !border-background" />
    <Handle type="source" position={Position.Bottom} id="false" style={{ left: "70%" }} className="!w-3 !h-3 !bg-[#FF453A] !border-2 !border-background" />
  </div>
);

export const BookingNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-booking">
    <NodeHeader color="#BF5AF2" label={data.label || "Booking"} icon="📅" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 text-xs text-muted-foreground">
      Calendar: {data.config?.calendar_name || data.config?.calendar_id || "Not configured"}
    </div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const WebhookNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-webhook">
    <NodeHeader color="#00E676" label={data.label || "Custom Webhook"} icon="⚡" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 space-y-1">
      <div className="text-xs text-muted-foreground">
        {data.config?.method || "POST"} {data.config?.url || "https://..."}
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const DelayNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-delay">
    <NodeHeader color="#BF5AF2" label={data.label || "Delay"} icon="⏱" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 text-xs text-muted-foreground">
      Wait {data.config?.duration || 0} {data.config?.unit || "minutes"}
    </div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const TagNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-tag">
    <NodeHeader color="#00E676" label={data.label || "Tag"} icon="🏷" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 text-xs text-muted-foreground">
      {data.config?.action || "add"} tag: {data.config?.tag_name || "Not set"}
    </div>
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
  </div>
);

export const EndNode = ({ data }) => (
  <div className={nodeBase} data-testid="node-end">
    <NodeHeader color="#FF453A" label={data.label || "End"} icon="⬛" />
    <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#27272A] hover:!bg-primary !border-2 !border-background" />
    <div className="p-3 space-y-1.5">
      <div className="text-xs text-muted-foreground">
        {data.config?.end_type === "handoff" ? "Hand off to human agent" : 
         data.config?.end_type === "complete" ? "Mark conversation complete" : 
         "End workflow"}
      </div>
      {data.config?.final_message && (
        <div className="text-xs text-foreground/70 italic line-clamp-2">
          "{data.config.final_message}"
        </div>
      )}
    </div>
  </div>
);

export const nodeTypes = {
  start: StartNode,
  objective: ObjectiveNode,
  conversation: ConversationNode,
  conditional: ConditionalNode,
  booking: BookingNode,
  webhook: WebhookNode,
  delay: DelayNode,
  tag: TagNode,
  end: EndNode,
};

// Evaluated per call so palette labels follow the active UI language
// (a module-level const would freeze whatever language was active at import time).
export const getNodePalette = () => [
  // Start is not in the palette: every workflow has exactly one, added automatically
  { type: "objective", label: i18n.t("nodes.objective"), color: "#00E676", icon: "◎" },
  { type: "conversation", label: i18n.t("nodes.conversation"), color: "#00E676", icon: "💬" },
  { type: "conditional", label: i18n.t("nodes.condition"), color: "#FFD60A", icon: "◇" },
  { type: "booking", label: i18n.t("nodes.booking"), color: "#BF5AF2", icon: "📅" },
  { type: "webhook", label: "Webhook", color: "#00E676", icon: "⚡" },
  { type: "delay", label: i18n.t("nodes.delay"), color: "#BF5AF2", icon: "⏱" },
  { type: "tag", label: i18n.t("nodes.tag"), color: "#00E676", icon: "🏷" },
  { type: "end", label: i18n.t("nodes.end"), color: "#FF453A", icon: "⬛" },
];
